import React from "react";
import { Link, useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  ArrowLeft,
  ArrowRight,
  ChevronRight,
  Sparkles,
  CheckCircle2,
  Quote,
  BookOpen,
} from "lucide-react";

import { caseStudies } from "@/data/caseStudies";
import Section from "@/components/shared/Section";
import Reveal from "@/components/shared/Reveal";
import GlowCard from "@/components/shared/GlowCard";
import Eyebrow from "@/components/shared/Eyebrow";

const CaseStudyDetail = () => {
  const { slug } = useParams();
  const study = caseStudies.find((item) => item.slug === slug);

  if (!study) {
    return (
      <div className="min-h-screen text-[var(--text-primary)]">
        <Section className="page-top text-center">
          <h1 className="text-3xl font-display font-bold mb-4">Case Study Not Found</h1>
          <p className="text-[var(--text-secondary)] mb-6 max-w-md mx-auto">
            The case study you're looking for doesn't exist or has been moved.
          </p>
          <Button asChild className="btn-primary rounded-full px-6">
            <Link to="/case-studies" className="inline-flex items-center gap-2">
              <ArrowLeft className="w-4 h-4" /> Back to Case Studies
            </Link>
          </Button>
        </Section>
      </div>
    );
  }

  return (
    <div className="min-h-screen text-[var(--text-primary)]">
      {/* Hero */}
      <section className="relative page-top pb-10 overflow-hidden aurora">
        <div className="container-page relative z-10">
          <Link
            to="/case-studies"
            className="inline-flex items-center gap-1.5 text-sm text-[var(--text-secondary)] hover:text-[var(--text-primary)] mb-6 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" /> All case studies
          </Link>

          <Reveal variant="up">
            <Eyebrow icon={BookOpen} className="mb-4">Case Study</Eyebrow>
          </Reveal>
          <Reveal variant="up" delay={0.05}>
            <h1 className="font-display font-bold text-4xl md:text-5xl lg:text-6xl mb-3 tracking-tight leading-[1.05] max-w-4xl">
              {study.title}
            </h1>
          </Reveal>
          <Reveal variant="up" delay={0.1}>
            <p className="text-xl gradient-text font-semibold">{study.result}</p>
          </Reveal>
        </div>
      </section>

      {/* Hero image */}
      <Section size="sm">
        <Reveal variant="scale">
          <div className="rounded-3xl overflow-hidden border border-[color:var(--border-default)]">
            <img
              src={study.image}
              alt={study.title}
              className="w-full h-72 md:h-[420px] object-cover"
            />
          </div>
        </Reveal>
      </Section>

      {/* Content */}
      <Section size="sm">
        <div className="grid lg:grid-cols-3 gap-7 max-w-6xl mx-auto">
          <Reveal variant="up" className="lg:col-span-2">
            <GlowCard className="p-8 sm:p-10 space-y-7">
              <div>
                <p className="text-[var(--text-secondary)] text-base leading-relaxed">{study.summary}</p>
              </div>

              <div>
                <h3 className="font-display font-semibold text-lg mb-2 text-[var(--text-primary)]">
                  Client Background
                </h3>
                <p className="text-[var(--text-secondary)] text-sm leading-relaxed">{study.clientBackground}</p>
              </div>

              <div>
                <h3 className="font-display font-semibold text-lg mb-2 text-[var(--text-primary)]">
                  Problem Statement
                </h3>
                <p className="text-[var(--text-secondary)] text-sm leading-relaxed">{study.problemStatement}</p>
              </div>

              <div>
                <h3 className="font-display font-semibold text-lg mb-3 text-[var(--text-primary)]">
                  Key Challenges
                </h3>
                <ul className="space-y-2.5">
                  {study.challenges.map((item) => (
                    <li key={item} className="flex items-start gap-2.5 text-sm text-[var(--text-secondary)]">
                      <span className="w-1.5 h-1.5 rounded-full bg-[var(--brand-primary-light)] mt-2 flex-shrink-0" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div>
                <h3 className="font-display font-semibold text-lg mb-2 text-[var(--text-primary)]">
                  Solution Approach
                </h3>
                <p className="text-[var(--text-secondary)] text-sm leading-relaxed">{study.solutionApproach}</p>
              </div>

              <div>
                <h3 className="font-display font-semibold text-lg mb-3 text-[var(--text-primary)]">
                  Tools & Technologies
                </h3>
                <div className="flex flex-wrap gap-2">
                  {study.toolsTech.map((tool) => (
                    <span
                      key={tool}
                      className="text-xs font-medium text-[var(--text-primary)] border border-[color:var(--border-brand)] bg-[color:var(--brand-primary)]/10 rounded-full px-3 py-1.5"
                    >
                      {tool}
                    </span>
                  ))}
                </div>
              </div>

              <div>
                <h3 className="font-display font-semibold text-lg mb-3 text-[var(--text-primary)]">
                  Measurable Results
                </h3>
                <ul className="space-y-2.5">
                  {study.results.map((item) => (
                    <li key={item} className="flex items-start gap-2.5 text-sm text-[var(--text-secondary)]">
                      <CheckCircle2 className="w-4 h-4 text-[var(--brand-accent-cyan)] mt-0.5 flex-shrink-0" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div>
                <h3 className="font-display font-semibold text-lg mb-2 text-[var(--text-primary)]">
                  Client Impact
                </h3>
                <p className="text-[var(--text-secondary)] text-sm leading-relaxed">{study.clientImpact}</p>
              </div>
            </GlowCard>
          </Reveal>

          <Reveal variant="up" delay={0.1}>
            <div className="space-y-5 lg:sticky lg:top-28">
              {/* Testimonial */}
              <GlowCard variant="liquid" className="aurora p-7 overflow-hidden">
                <Quote className="w-8 h-8 text-[var(--brand-primary-light)]/40 mb-3" />
                <p className="text-[var(--text-primary)] text-sm italic leading-relaxed mb-4">
                  "{study.testimonial.quote}"
                </p>
                <div className="text-sm">
                  <p className="font-semibold text-[var(--text-primary)]">{study.testimonial.name}</p>
                  <p className="text-[var(--text-secondary)] text-xs">{study.testimonial.title}</p>
                </div>
              </GlowCard>

              {/* CTA */}
              <GlowCard className="p-7">
                <Eyebrow icon={Sparkles} className="mb-3">Get started</Eyebrow>
                <h4 className="font-display font-semibold text-lg mb-3">
                  Want similar results?
                </h4>
                <p className="text-[var(--text-secondary)] text-sm mb-5">
                  Let's talk about your goals and map a measurable AI roadmap.
                </p>
                <div className="flex flex-col gap-2">
                  <Button asChild className="btn-primary rounded-full px-6 h-11">
                    <Link to="/contact" className="inline-flex items-center gap-2">
                      Contact us <ArrowRight className="w-4 h-4" />
                    </Link>
                  </Button>
                  <Button asChild variant="outline" className="btn-outline rounded-full px-6 h-11">
                    <Link to="/case-studies" className="inline-flex items-center gap-2">
                      All case studies <ChevronRight className="w-4 h-4" />
                    </Link>
                  </Button>
                </div>
              </GlowCard>
            </div>
          </Reveal>
        </div>
      </Section>
    </div>
  );
};

export default CaseStudyDetail;
